export enum Analytic {
    CPPOpt = "cppopt",
    CPPOpt_FLEX = "cppopt_flex",
    MAPOpt = "mapopt",
    MAPOpt_FLEX = "mapopt_flex",
    PRx = "prx",
    COx = "cox",
    ROLLING_AVERAGE = "rolling_average",
    AUTO_SD_DETECTION = "auto_sd_detection",
    RESAMPLE = "resample",
    QEEG_DSA = "qeeg_dsa"
}


export const getAnalyticDisplayName = (analytic: Analytic | undefined) => {
    switch (analytic) {
        case Analytic.PRx: return "PRx"
        case Analytic.COx: return "COx"
        case Analytic.QEEG_DSA: return "qEEG DSA"
        case Analytic.CPPOpt: 
        case Analytic.CPPOpt_FLEX:
            return "CPPOpt"
        case Analytic.MAPOpt: 
        case Analytic.MAPOpt_FLEX:
            return "MAPOpt"
        case Analytic.ROLLING_AVERAGE: return "Rolling average"
        default: return ""
    }
}

type CPPOptArgs = {
    ABP: string
    ICP: string
    prxThreshold: number
}

type MAPOptArgs = {
    ABP: string
    stO2Left: string
    stO2Right: string
    enableLeft: boolean
    enableRight: boolean
    coxThreshold: number
}


// Analytics whose output is a 2D frequency-by-time composite and therefore
// render as a spectrogram. The spectrogram renderer itself is metric-agnostic;
// add future 2D qEEG metrics here rather than special-casing QEEG_DSA.
export const SPECTROGRAM_ANALYTICS: ReadonlySet<Analytic> = new Set([Analytic.QEEG_DSA])

export const isSpectrogramAnalytic = (analytic: Analytic | undefined): boolean =>
    analytic !== undefined && SPECTROGRAM_ANALYTICS.has(analytic)

export type DSADisplayScale = "power" | "voltage" | "sqrt_voltage"
export const DEFAULT_DSA_DISPLAY_SCALE: DSADisplayScale = "sqrt_voltage"

export const QEEG_DSA_CHANNEL_GROUPS = [
    { id: "LeftHemisphere", name: "Left Hemisphere" },
    { id: "RightHemisphere", name: "Right Hemisphere" },
    { id: "Fp1-F7", name: "Fp1-F7" },
    { id: "F7-T7", name: "F7-T7" },
    { id: "T7-P7", name: "T7-P7" },
    { id: "P7-O1", name: "P7-O1" },
    { id: "Fp1-F3", name: "Fp1-F3" },
    { id: "F3-C3", name: "F3-C3" },
    { id: "C3-P3", name: "C3-P3" },
    { id: "P3-O1", name: "P3-O1" },
    { id: "Fp2-F8", name: "Fp2-F8" },
    { id: "F8-T8", name: "F8-T8" },
    { id: "T8-P8", name: "T8-P8" },
    { id: "P8-O2", name: "P8-O2" },
    { id: "Fp2-F4", name: "Fp2-F4" },
    { id: "F4-C4", name: "F4-C4" },
    { id: "C4-P4", name: "C4-P4" },
    { id: "P4-O2", name: "P4-O2" },
    { id: "Fz-Cz", name: "Fz-Cz" },
    { id: "Cz-Pz", name: "Cz-Pz" },
] as const

// Derived from the runtime list so the two cannot drift.
export type QEEGDSAChannelGroupId = typeof QEEG_DSA_CHANNEL_GROUPS[number]["id"]

export type QEEGDSAChannelGroup = {
    id: QEEGDSAChannelGroupId
    name: string
}

export const DEFAULT_QEEG_DSA_CHANNEL_GROUP_ID: QEEGDSAChannelGroupId = "LeftHemisphere"

export const getQEEGDSAChannelGroupName = (channelGroupId: QEEGDSAChannelGroupId): string => {
    return QEEG_DSA_CHANNEL_GROUPS.find(channelGroup => channelGroup.id === channelGroupId)?.name ?? channelGroupId
}

export const getQEEGDSATraceName = (channelGroupId: QEEGDSAChannelGroupId): string => {
    return `${getQEEGDSAChannelGroupName(channelGroupId)} DSA`
}

export type QEEGDSAAnalysis = BaseOnDemandAnalysis & {
    analytic: Analytic.QEEG_DSA
    channelGroupId: QEEGDSAChannelGroupId
    displayScale: DSADisplayScale
}


// On-demand analytics are calculated along with the page request at request time.
type BaseOnDemandAnalysis = {
    analytic: Analytic // The analytic that we are calculating
}

// Analytics that sweep a calculation window over time. Not every analytic does.
export type TimedOnDemandAnalysis = BaseOnDemandAnalysis & {
    calculationPeriodMs: number // How often we should calculate the analytic
    calculationWindowMs: number // How much data we need for the analytic
}


// What a caller holds when it has "some analysis". A union rather than the shared
// base so that discriminating on `analytic` narrows to a concrete analysis, and so
// that a DSA analysis is assignable wherever one is expected.
//
// qEEG DSA sits outside TimedOnDemandAnalysis because it has no calculation window
// or period: its equivalents are the STFT window and epoch length, which -- like the
// rest of the DSA algorithm parameters -- query owns and the client cannot set.
export type OnDemandAnalysis = TimedOnDemandAnalysis | QEEGDSAAnalysis

export const isQEEGDSASourceModality = (rawDataKey: string): boolean => rawDataKey.startsWith("qEEG_DSA")

// Offline DSA archive files are `qEEG_DSA,{group},SampleSeries,CompositeFloat,MCP,data`.
export const isOfflineQEEGDSAFileName = (fileName: string | undefined): boolean => {
    const parts = (fileName ?? "").split(",")
    return parts[0] === "qEEG_DSA" && parts[2] === "SampleSeries" && parts[3] === "CompositeFloat" && parts[4] === "MCP"
}

// The modality-list key is underscore-normalized (`qEEG_DSA_LeftHemisphere`), so the
// channel group is whatever follows the prefix. "(pre-computed)" keeps a source trace
// distinguishable from the analytic's "{group} DSA" when both are on a display.
export const getSourceQEEGDSATraceName = (rawDataKey: string): string => {
    const groupId = rawDataKey.replace(/^qEEG_DSA[_,]/, "").split(",")[0]
    const groupName = QEEG_DSA_CHANNEL_GROUPS.find(group => group.id === groupId)?.name ?? groupId
    return `${groupName} DSA (pre-computed)`
}

export const isQEEGDSAAnalysis = (analysis: OnDemandAnalysis | undefined): analysis is QEEGDSAAnalysis => (
    analysis?.analytic === Analytic.QEEG_DSA
)

// TODO(v2): query moves to a canonical 0-70 Hz band so the display band can change
// without refetching. That needs a real crop here -- a row offset, not just a
// prefix take -- before the displayed minimum can differ from the computed one.
export const QEEG_DSA_DISPLAY_MIN_FREQ_HZ = 0
export const QEEG_DSA_DISPLAY_MAX_FREQ_HZ = 20
export const QEEG_DSA_HZ_STEP = 0.5

export type QEEGDSAAnalysisRequest = Omit<QEEGDSAAnalysis, "displayScale">

export type OnDemandAnalysisRequest = TimedOnDemandAnalysis | QEEGDSAAnalysisRequest

// Picks the request fields explicitly: this is the wire contract, and query builds
// both its cache key and its shared-compute batch key from whatever arrives.
//
// displayScale is excluded because it is a render transform: dropping it keeps the
// data key, and therefore the cache entry, stable when the scale changes.
export const getQEEGDSARequestAnalysis = ({ analytic, channelGroupId }: QEEGDSAAnalysis): QEEGDSAAnalysisRequest => (
    { analytic, channelGroupId }
)


// Band centers of the displayed crop. Derived, never persisted: a saved copy would
// silently disagree with the renderer the moment the display band changes.
export const getDSAFrequencyLabels = (): string[] => {
    const bandCount = Math.max(
        Math.round((QEEG_DSA_DISPLAY_MAX_FREQ_HZ - QEEG_DSA_DISPLAY_MIN_FREQ_HZ) / QEEG_DSA_HZ_STEP),
        0
    )
    return Array.from({ length: bandCount }, (_, index) =>
        (QEEG_DSA_DISPLAY_MIN_FREQ_HZ + (index + 0.5) * QEEG_DSA_HZ_STEP).toFixed(2)
    )
}

// The offline archive contract is immutable: every stored study frame carries 40
// bins at 0-20 Hz. Deliberately NOT the analytic's display constants above since
// the analytic's display range is subject to change in the future
export const getSourceDSAFrequencyLabels = (): string[] =>
    Array.from({ length: 40 }, (_, index) => ((index + 0.5) * 0.5).toFixed(2))


export type PRxAnalysis = TimedOnDemandAnalysis & {
    analytic: Analytic.PRx
    ABP: string
    ICP: string
}


export type COxAnalysis = TimedOnDemandAnalysis & {
    analytic: Analytic.COx
    StO2Left: string
    StO2Right: string
    enableLeft: boolean
    enableRight: boolean
    ABP: string
}


export type CPPOptAnalysis = TimedOnDemandAnalysis & CPPOptArgs & {
    analytic: Analytic.CPPOpt
}


export type CPPOptFlexAnalysis = TimedOnDemandAnalysis & {
    analytic: Analytic.CPPOpt_FLEX
    ABP: string
    ICP: string
    prxThreshold: number
}


export type MAPOptAnalysis = TimedOnDemandAnalysis & MAPOptArgs & {
    analytic: Analytic.MAPOpt
}


export type MAPOptFlexAnalysis = TimedOnDemandAnalysis & MAPOptArgs & {
    analytic: Analytic.MAPOpt_FLEX
}


export type AutoSDAnalysis = TimedOnDemandAnalysis & {
    analytic: Analytic.AUTO_SD_DETECTION
    channels: string[]
}


export type GenericOnDemandAnalysis = TimedOnDemandAnalysis & {
    rawDataKey: string
}


export const isGenericAnalytic = (analytic: Analytic | undefined) => {
    return analytic && new Set([
        Analytic.ROLLING_AVERAGE,
    ]).has(analytic)
}
